import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { IoCloseSharp } from "react-icons/io5";
import { BiSolidError } from "react-icons/bi";
import { RiDeleteBin6Fill } from "react-icons/ri";
import AdminPagination from "../../../component/pagination/AdminPagination";
import SkeletonLoading from "../../../component/SkeletonLoading";
import {
  useGetPaymentHistoryQuery,
  useRetryPaymentMutation,
  useDeleteParentPaymentMutation,
} from "../../../store/api/school/PaymentHistorySlice";
import {
  capitalizeFirstLetter,
  formatDateToLocal,
} from "../../../utils/dateAndTimeConversion";
const initialPagination = { limit: 10, page: 1, total: "", totalPages: "" };

const statusClass = (status) => {
  if (!status) return "status unavailable";
  const s = (status || "").toLowerCase();
  if (s === "completed" || s === "paid") return "status bg-success text-white";
  if (s === "cancelled" || s === "rejected")
    return "status bg-danger text-white";
  if (s === "partial") return "status bg-warning text-dark";
  return "status unavailable";
};

const paymentTypeLabel = (type) => {
  if (type === "full_payment") return "Full";
  if (type === "partial_payment") return "Partial";
  if (type === "student_fee") return "Student Fee";
  return type || "—";
};

const ParentPaymentHistory = () => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(initialPagination);
  const [retryingId, setRetryingId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const [retryPayment] = useRetryPaymentMutation();
  const [deleteParentPayment, { isLoading: isDeleting }] =
    useDeleteParentPaymentMutation();

  const handleDelete = async () => {
    try {
      await deleteParentPayment(deleteTargetId).unwrap();
      toast.success("Payment record deleted.");
      setDeleteTargetId(null);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete payment record.");
      setDeleteTargetId(null);
    }
  };

  const handleRetry = async (orderId) => {
    toast.dismiss();
    setRetryingId(orderId);
    try {
      const response = await retryPayment(orderId).unwrap();
      if (response?.data?.already_completed) {
        toast.success(
          "Your payment was already processed! Your plan is now active.",
        );
      } else if (response?.data?.iframe_url) {
        window.open(response.data.iframe_url, "_blank");
      } else {
        toast.error("Could not get payment link. Please try again.");
      }
    } catch (err) {
      toast.error(err?.data?.message || "Retry failed. Please try again.");
    } finally {
      setRetryingId(null);
    }
  };

  const { data, isSuccess, isLoading, isFetching } = useGetPaymentHistoryQuery({
    page: pagination.page,
    limit: pagination.limit,
  });

  useEffect(() => {
    if (isSuccess && data?.data) {
      setHistory(data.data.history || []);
      setSummary(data.data.summary || null);
      setPagination((prev) => ({
        ...prev,
        total: data.data.pagination.total,
        page: data.data.pagination.page,
        limit: data.data.pagination.limit,
        totalPages: data.data.pagination.pages,
      }));
    }
  }, [data, isSuccess]);

  const handlePageChange = (page) =>
    setPagination((prev) => ({ ...prev, page }));
  const handleLimitChange = (limit) =>
    setPagination((prev) => ({ ...prev, limit, page: 1 }));

  const currSym = (c) => (c === "CAD" ? "C$" : "$");
  const summaryCurrency = history[0]?.currency || "USD";

  return (
    <>
      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard">
              <div className="summaryLabel">
                {t("total")} {t("amount")}
              </div>
              <div className="summaryValue">
                {currSym(summaryCurrency)}{parseFloat(summary.total_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard summaryCardSuccess">
              <div className="summaryLabel">
                {t("paid")} {t("amount")}
              </div>
              <div className="summaryValue">
                {currSym(summaryCurrency)}{parseFloat(summary.paid_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard summaryCardDanger">
              <div className="summaryLabel">
                {t("outstanding")} {t("amount")}
              </div>
              <div className="summaryValue">
                {currSym(summaryCurrency)}{parseFloat(summary.due_amount || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard">
              <div className="summaryLabel">
                {t("payment")} {t("status")}
              </div>
              <div className="summaryValue">
                <span className={statusClass(summary.payment_status)}>
                  {summary?.payment_status &&
                    capitalizeFirstLetter(summary.payment_status)}
                </span>
              </div>
              {summary.remaining_installments > 0 && (
                <div className="summaryNote">
                  {summary.remaining_installments} {t("installments")}{" "}
                  {t("remaining")}
                </div>
              )}
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard">
              <div className="summaryLabel">
                {t("last")} {t("payment")} {t("date")}
              </div>
              <div className="summaryValue summaryValueSm">
                {summary.last_payment_date
                  ? formatDateToLocal(summary.last_payment_date)
                  : "—"}
              </div>
            </div>
          </div>
          <div className="col-sm-6 col-lg-3">
            <div className="summaryCard summaryCardWarning">
              <div className="summaryLabel">
                {t("next")} {t("payment")} {t("due")}
              </div>
              <div className="summaryValue summaryValueSm">
                {summary.next_payment_due
                  ? formatDateToLocal(summary.next_payment_due)
                  : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Total count */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="dbSubHd">
          {t("total")}: {pagination.total}
        </div>
      </div>

      {/* Transactions Table */}
      <div className="table m-0">
        <table>
          <thead>
            <tr>
              <th>
                {t("Sr")}.{t("no")}.
              </th>
              <th>
                {t("order")} {t("number")}
              </th>
              <th>{t("amount")}</th>
              <th>{t("currency")}</th>
              <th className="text-nowrap">
                {t("payment")} {t("type")}
              </th>
              <th>
                {t("transaction")} {t("id")}
              </th>

              <th className="text-nowrap">
                {t("created")} {t("by")}
              </th>
              <th className="text-nowrap">
                {t("paid")} {t("to")}
              </th>
              <th>{t("date")}</th>
              <th className="text-nowrap">
                {t("payment")} {t("status")}
              </th>
              <th>{t("action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              <SkeletonLoading MainLength={10} length={11} />
            ) : history?.length > 0 ? (
              history.map((item, i) => (
                <tr key={item.id}>
                  <td>{(pagination.page - 1) * pagination.limit + i + 1}</td>
                  <td className="text-nowrap">{item.order_number || "—"}</td>
                  <td className="text-nowrap">
                    {currSym(item.currency)}{(item.amount_cents / 100).toFixed(2)}
                  </td>
                  <td className="text-nowrap">{item.currency}</td>
                  <td className="text-nowrap">
                    {paymentTypeLabel(item.payment_type)}
                  </td>
                  <td className="text-nowrap">{item.transaction_id || "—"}</td>

                  {/* Created By */}
                  <td className="text-nowrap">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <strong>{item.creator?.name || "—"}</strong>
                      <small
                        style={{
                          fontSize: 11,
                          color: "#94a3b8",
                          textTransform: "capitalize",
                        }}
                      >
                        {item.creator?.role}
                      </small>
                    </div>
                  </td>

                  <td className="text-nowrap">
                    {item.effective_bank ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <span>{item.effective_bank.bank_name || "—"}</span>
                        {item.effective_bank.routed_through_admin && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              padding: "1px 6px",
                              borderRadius: 10,
                              background: "#fde8c8",
                              color: "#c8900a",
                              border: "1px solid #f0b342",
                              width: "fit-content",
                            }}
                          >
                            {t("via")} {t("admin")}
                          </span>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-nowrap">
                    {formatDateToLocal(item.created_at)}
                  </td>
                  <td className="text-nowrap">
                    <span className={statusClass(item.payment_status)}>
                      {item.payment_status}
                    </span>
                  </td>
                  <td className="text-nowrap">
                    <div className="d-flex gap-1 align-items-center">
                      {/* {item.payment_status?.toLowerCase() === "created" && (
                        <button
                          className="btn btn-warning btn-sm text-white"
                          onClick={() => handleRetry(item.id)}
                          disabled={retryingId === item.id}
                        >
                          {retryingId === item.id ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                            />
                          ) : (
                            t("retry") || "Retry"
                          )}
                        </button>
                      )} */}
                      {item.payment_status?.toLowerCase() !== "completed" && (
                        <Button
                          className="rounded-circle"
                          onClick={() => setDeleteTargetId(item.id)}
                          title={t("delete")}
                        >
                          <RiDeleteBin6Fill />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan="11">
                  {t("no")} {t("details")} {t("found")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AdminPagination
        totalPages={pagination.totalPages}
        currentPage={pagination.page}
        limit={pagination.limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Delete Confirmation Modal */}
      <Modal show={!!deleteTargetId} centered>
        <Button className="closePopup" onClick={() => setDeleteTargetId(null)}>
          <IoCloseSharp />
        </Button>
        <div className="popupBox p-3 p-md-5">
          <div className="text-center mb-3 errorIcon">
            <BiSolidError />
          </div>
          <div className="text-center mb-4 deleteHd">
            {`${t("are")} ${t("you")} ${t("sure")} ${t("want")} ${t("to")} ${t("delete")} ${t("this")} ${t("payment")} ${t("record")}?`}
          </div>
          <div className="text-center">
            <Button
              className="smallBtn cancel me-3"
              onClick={() => setDeleteTargetId(null)}
            >
              {t("btns.cancel")}
            </Button>
            <Button
              className="smallBtn auto"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                />
              ) : (
                t("btns.delete")
              )}
            </Button>
          </div>
        </div>
      </Modal>

      <style>{`
        .summaryCard {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px 20px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .summaryCardSuccess  { border-left: 4px solid #22c55e; }
        .summaryCardDanger   { border-left: 4px solid #ef4444; }
        .summaryCardWarning  { border-left: 4px solid #f0b241; }
        .summaryLabel {
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6b7280;
          margin-bottom: 6px;
          font-weight: 600;
        }
        .summaryValue {
          font-size: 1.25rem;
          font-weight: 700;
          color: #111827;
        }
        .summaryValueSm {
          font-size: 0.95rem;
          font-weight: 600;
          color: #374151;
        }
        .summaryNote {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 4px;
        }
      `}</style>
    </>
  );
};

export default ParentPaymentHistory;
